import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import authService from '../../services/authService'
import { Mail, Lock, ArrowRight, Loader2, Eye, EyeOff, UserCheck, Settings, Globe, Briefcase, Search, BarChart3, ShieldCheck, Handshake } from 'lucide-react'
import Logo from '../../components/Logo'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginForm = z.infer<typeof loginSchema>

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      const response = await authService.login(data)
      login(response.user, response.tokens)
      toast.success('Welcome back!')
      navigate(from, { replace: true })
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || 'Invalid credentials')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex">
      {/* Left side - How It Works */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 p-12 flex-col overflow-y-auto">
        <div className="mb-8">
          <Logo to="/" height={48} />
        </div>

        <h2 className="text-2xl font-bold text-white mb-8">How It Works</h2>

        {/* Join as an Expert */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-accent-400 mb-4">Join as an Expert</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-4.5 w-4.5 text-accent-400" />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Create a Verified Profile</h4>
                <p className="text-primary-300 text-xs leading-relaxed">Showcase your academic background, specialized expertise, certifications, and service offerings.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
                <Settings className="h-4.5 w-4.5 text-accent-400" />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Set Your Services & Rates</h4>
                <p className="text-primary-300 text-xs leading-relaxed">Independently define your session formats, pricing, and availability.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
                <Globe className="h-4.5 w-4.5 text-accent-400" />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Get Discovered Globally</h4>
                <p className="text-primary-300 text-xs leading-relaxed">Clients filter by discipline, methods, and region to identify relevant expertise.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
                <Briefcase className="h-4.5 w-4.5 text-accent-400" />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Deliver Structured Engagements</h4>
                <p className="text-primary-300 text-xs leading-relaxed">Mentorship sessions, lectures, technical consulting, and project-based work are booked and delivered securely through the platform.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-primary-600/50 mb-8" />

        {/* Find an Expert */}
        <div>
          <h3 className="text-lg font-semibold text-accent-400 mb-4">Find an Expert</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
                <Search className="h-4.5 w-4.5 text-accent-400" />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Browse & Filter Experts</h4>
                <p className="text-primary-300 text-xs leading-relaxed">Search by discipline, service offering, skills, sub-specialty, certifications, and region.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="h-4.5 w-4.5 text-accent-400" />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Compare Profiles Transparently</h4>
                <p className="text-primary-300 text-xs leading-relaxed">Review academic background, credentials, service offerings, and pricing before booking.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-4.5 w-4.5 text-accent-400" />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Book Securely</h4>
                <p className="text-primary-300 text-xs leading-relaxed">Schedule sessions and complete payment directly through the platform.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
                <Handshake className="h-4.5 w-4.5 text-accent-400" />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Engage with Confidence</h4>
                <p className="text-primary-300 text-xs leading-relaxed">Access structured mentorship, lectures, consulting, or project-based support regardless of geographic location.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-secondary-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <Logo to="/" height={40} />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-secondary-900">Welcome Back</h1>
            <p className="text-secondary-500 mt-2">Sign in to continue to your account</p>
          </div>

          <div className="bg-white rounded-2xl shadow-card border border-secondary-200 p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label htmlFor="email" className="label">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
                  <input
                    id="email"
                    type="email"
                    className={`input pl-12 ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                    placeholder="you@example.com"
                    {...register('email')}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1.5">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="label">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`input pl-12 pr-12 ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                    placeholder="Enter your password"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1.5">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-secondary-300 text-accent-500 focus:ring-accent-500/20"
                  />
                  <span className="text-sm text-secondary-600">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-accent-600 hover:text-accent-700 font-medium">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-accent w-full py-3 text-base font-semibold flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-secondary-100">
              <p className="text-center text-secondary-600">
                Don't have an account?{' '}
                <Link to="/register" className="text-accent-600 hover:text-accent-700 font-semibold">
                  Sign up
                </Link>
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-secondary-400 mt-6">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-secondary-500 hover:text-secondary-700">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-secondary-500 hover:text-secondary-700">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
